import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB chunks

/**
 * Upload a large file in chunks with progress tracking.
 * @param {File} file - The file to upload
 * @param {function} onProgress - Callback with { percent, chunkIndex, totalChunks, status }
 * @returns {Promise<{path, id, filename, size}>}
 */
export async function uploadFileChunked(file, onProgress) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // 1. Init session
  onProgress?.({ percent: 0, status: 'Initializing...' });
  const initRes = await axios.post(`${API}/upload/init-chunked`, {
    filename: file.name,
    content_type: file.type || 'video/mp4',
    total_size: file.size,
    total_chunks: totalChunks,
  }, { headers });
  const { upload_id } = initRes.data;

  // 2. Upload chunks sequentially
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('file', chunk, `chunk_${i}`);

    await axios.post(`${API}/upload/chunk/${upload_id}/${i}`, formData, {
      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
    });

    const percent = Math.round(((i + 1) / totalChunks) * 90); // 0-90% for chunks
    onProgress?.({ percent, chunkIndex: i + 1, totalChunks, status: `Uploading chunk ${i + 1}/${totalChunks}` });
  }

  // 3. Finalize
  onProgress?.({ percent: 95, status: 'Finalizing...' });
  const finalRes = await axios.post(`${API}/upload/finalize/${upload_id}`, {}, { headers });
  onProgress?.({ percent: 100, status: 'Complete' });

  return finalRes.data;
}
